import { customAlphabet } from "nanoid";
import { IMovementElementItem, TModuleOptions, TTaxResume } from "../models/storageModel";
import dayjs from "dayjs";

export function createId(length: number) {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ"
  const nanoid = customAlphabet(alphabet, length)
  let str = nanoid(length)

  if (str.startsWith('-') || str.startsWith('_')) {
    str = 'A' + str.slice(1);
  }
  if (str.endsWith('-') || str.endsWith('_')) {
    str = str.slice(0, -1) + 'z';
  }
  return str
}

export function roundValue(value: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function addToUniquesArray(array: any[], element: any, id: any, position: 'start' | 'end' = 'end') {
  array = array.filter((item: any) => !JSON.stringify(item).includes(id));
  if (position === 'start') {
    array.unshift(element);
  } else {
    array.push(element);
  }
  return array;
}

export function removeDuplicatesByKey(array: any, key: any) {
  const seenKeys = new Set();
  return array.filter((item: any) => {
    const keyValue = item[key];
    if (seenKeys.has(keyValue)) {
      return false;
    }
    seenKeys.add(keyValue);
    return true;
  });
}

export function getTotalTaxes(itemList: IMovementElementItem[]): TTaxResume[] {
  const taxMap = new Map<string, TTaxResume>();
  itemList.forEach(item => {
    if (item.taxes_list) {
      item.taxes_list.forEach(tax => {
        if (taxMap.has(tax.key)) {
          const existing = taxMap.get(tax.key)!;
          taxMap.set(tax.key, {
            ...existing,
            tax_amount: existing.tax_amount + tax.tax_amount
          });
        } else {
          taxMap.set(tax.key, { ...tax });
        }
      });
    }
  });
  return Array.from(taxMap.values());
}

export function getItemTaxes(item: IMovementElementItem, taxesOptions: TModuleOptions[], type: "SELL_TAX" | "PURCHASE_TAX") {
  return item.module_options
    .filter(el => el.sub_type === type && el.is_active)
    .map(taxModuleOption => {
      const taxConfig = taxesOptions.find(el => el.id === taxModuleOption.option_id);
      if (!taxConfig) return null;

      const tax_amount = taxConfig.type === "TAX_RATE" ?
        (taxConfig.numeric_value || 0) / 100 * ((item.quantity * item.unit_price) * (1 - item.discount_rate / 100)) :
        taxConfig.numeric_value || 0;
      return {
        id: taxConfig.id || 0,
        key: taxConfig.key || "",
        tax_amount: roundValue(tax_amount) || 0,
        description: taxConfig.string_value || ""
      } as TTaxResume;
    })
    .filter((tax): tax is TTaxResume => tax !== null);
}

export function textNumberFormater(text: string | number, format: string = 'money') {
  if (text === 0) text = "0"
  text = text || ""
  if (format === "money") {
    return text.toLocaleString("es-CO")
  }
  return text.toString()
}

export function customNanoId(size: number = 10) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const nanoid = customAlphabet(alphabet, 10);
  return nanoid(size)
}

/**
 * Formatea los valores de un input numerico para convertirlo a separadores de mil por puntos y de coma para decimales
 * @param value 
 * @returns 
 */
export function formatInputNumber(value: number | undefined) {
  if (!value) return "";
  const [integerPart, decimalPart] = value.toString().split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decimalPart ? `${formattedInteger},${decimalPart}` : formattedInteger;
};

/**
 * Formatea los valores de un input numerico para convertirlo al valor estandar de Javascript
 * @param value 
 * @returns 
 */
export function parseInputNumber(value: any) {
  if (!value) return "";
  return value.replace(/\./g, "").replace(",", ".");
};

/**
 * Resize long texts and add ...
 * @param text
 * @param maxLength
 * @returns 
 */
export function resizeText(text: any, maxLength: number) {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
}

export function getDocumentLabels(type: string | undefined) {
  switch (type) {
    case 'CREDIT_NOTE':
      return { title: 'Nota de Crédito', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'DEBIT_NOTE':
      return { title: 'Nota de Débito', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'SALE_INVOICE':
      return { title: 'Comprobante de Venta', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'PURCHASE_ORDER':
      return { title: 'Orden de Compra', counterparty: 'Proveedor:', user: 'Solicitante:' };
    case 'PURCHASE_RECEIPT':
      return { title: 'Comprobante de Compra', counterparty: 'Proveedor:', user: 'Recibido por:' };
    case 'PURCHASE_REQUEST':
      return { title: 'Solicitud de Compra', counterparty: 'Proveedor:', user: 'Solicitante:' };
    case 'QUOTATION':
      return { title: 'Cotización', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'TRANSFER':
      return { title: 'Comprobante de Transferencia', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'ADJUSTMENT':
      return { title: 'Comprobante de Ajuste', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'RETURN_IN':
      return { title: 'Devolución en Venta', counterparty: 'Cliente:', user: 'Recibido por:' };
    case 'RETURN_OUT':
      return { title: 'Devolución en Compra', counterparty: 'Proveedor:', user: 'Enviado por:' };
    case 'INITIAL_INVENTORY':
      return { title: 'Inventario Inicial', counterparty: 'Responsable:', user: 'Registrado por:' };
    case 'PARTNER_CONTRIBUTION':
      return { title: 'Aporte de Socio', counterparty: 'Socio:', user: 'Recibido por:' };
    case 'WASTE':
      return { title: 'Baja de Inventario', counterparty: 'Responsable:', user: 'Registrado por:' };
    case 'PRODUCTION_ENTRY':
      return { title: 'Entrada de Producción', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'PRODUCTION_EXIT':
      return { title: 'Salida de Producción', counterparty: 'Responsable:', user: 'Realizado por:' };
    default:
      return { title: 'Comprobante de Movimiento', counterparty: 'Tercero:', user: 'Usuario:' };
  }
}

/**
 * Formats a date/timestamp to a standard UI display string (default 'DD/MM/YYYY hh:mm A')
 */
export function formatDateTime(dateStr?: string | number | Date | null, format: string = 'DD/MM/YYYY hh:mm A'): string {
  if (!dateStr) return '-';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format(format) : '-';
}

/**
 * Formats a date to 'DD/MM/YYYY' for UI display
 */
export function formatDate(dateStr?: string | number | Date | null, format: string = 'DD/MM/YYYY'): string {
  return formatDateTime(dateStr, format);
}